/**
 * Chart maths, kept out of the components.
 *
 * Pure functions: no React, no DOM. The charts stay about drawing, and the
 * awkward parts — axis ticks that land on round numbers, a domain that does
 * not collapse when every value is identical — are solved once.
 */

/** Map a value from a data domain onto a pixel range. */
export function scaleLinear(domain, range) {
  const [d0, d1] = domain;
  const [r0, r1] = range;
  const span = d1 - d0 || 1; // a flat series must not divide by zero
  const fn = (value) => r0 + ((value - d0) / span) * (r1 - r0);
  fn.invert = (pixel) => d0 + ((pixel - r0) / (r1 - r0 || 1)) * span;
  fn.domain = domain;
  fn.range = range;
  return fn;
}

/**
 * Axis ticks on human numbers — 0, 20, 40 rather than 0, 17.3, 34.6.
 * Y-axis ticks carry the values that are not directly labelled, so they have
 * to be readable at a glance.
 */
export function niceTicks(min, max, count = 5) {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return { ticks: [0, 1], domain: [0, 1] };
  if (min === max) {
    const pad = Math.abs(min) * 0.1 || 1;
    min -= pad;
    max += pad;
  }

  const rawStep = (max - min) / Math.max(1, count);
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const normalized = rawStep / magnitude;
  const step = (normalized >= 5 ? 10 : normalized >= 2 ? 5 : normalized >= 1 ? 2 : 1) * magnitude;

  const start = Math.floor(min / step) * step;
  const end = Math.ceil(max / step) * step;

  const ticks = [];
  for (let value = start; value <= end + step / 2; value += step) {
    // Floating point leaves 0.30000000000000004 lying around; round to the
    // step's own precision so tick labels are clean.
    ticks.push(Number(value.toFixed(10)));
  }

  return { ticks, domain: [start, end], step };
}

/** Extent across several series, ignoring nulls. */
export function extent(series, accessor) {
  let min = Infinity;
  let max = -Infinity;
  for (const points of series) {
    for (const point of points) {
      const value = accessor(point);
      if (value === null || value === undefined || Number.isNaN(value)) continue;
      if (value < min) min = value;
      if (value > max) max = value;
    }
  }
  if (min === Infinity) return [0, 1];
  return [min, max];
}

/**
 * Break the line where the device stopped reporting.
 *
 * A telematics feed has real gaps: a vehicle parked overnight, a device
 * offline for an hour, a SIM out of coverage. Joining the last point before a
 * gap to the first point after it draws a smooth line across hours of missing
 * data and invents a journey that did not happen — which is exactly what a
 * reader will believe.
 *
 * A gap is "real" when it is several times the feed's own typical interval,
 * so this adapts to a device reporting every 10 seconds and one reporting
 * every 5 minutes without being told which it is.
 */
export function insertGaps(points, { factor = 4, minGapMs = 60_000 } = {}) {
  if (!points || points.length < 3) return points || [];

  const deltas = [];
  for (let i = 1; i < points.length; i += 1) deltas.push(points[i].t - points[i - 1].t);
  const sorted = [...deltas].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)] || 0;
  if (!median) return points;

  const threshold = Math.max(minGapMs, median * factor);

  const out = [points[0]];
  for (let i = 1; i < points.length; i += 1) {
    if (points[i].t - points[i - 1].t > threshold) {
      // A null just after the last real point ends the segment there.
      out.push({ t: points[i - 1].t + 1, value: null, gap: true });
    }
    out.push(points[i]);
  }
  return out;
}

/**
 * An SVG path for a line, breaking at nulls rather than drawing through them.
 */
export function linePath(points, x, y) {
  let path = '';
  let pen = false;
  for (const point of points) {
    if (point.value === null || point.value === undefined || Number.isNaN(point.value)) {
      pen = false;
      continue;
    }
    const px = x(point.t);
    const py = y(point.value);
    path += `${pen ? 'L' : 'M'}${px.toFixed(2)} ${py.toFixed(2)}`;
    pen = true;
  }
  return path;
}

/** The same points as a closed area down to the baseline, gaps included. */
export function areaPath(points, x, y, baselineY) {
  const segments = [];
  let current = [];

  for (const point of points) {
    if (point.value === null || point.value === undefined || Number.isNaN(point.value)) {
      if (current.length) segments.push(current);
      current = [];
      continue;
    }
    current.push(point);
  }
  if (current.length) segments.push(current);

  return segments
    .filter((segment) => segment.length > 1)
    .map((segment) => {
      const top = segment
        .map((p, i) => `${i ? 'L' : 'M'}${x(p.t).toFixed(2)} ${y(p.value).toFixed(2)}`)
        .join('');
      const first = x(segment[0].t).toFixed(2);
      const last = x(segment[segment.length - 1].t).toFixed(2);
      return `${top}L${last} ${baselineY.toFixed(2)}L${first} ${baselineY.toFixed(2)}Z`;
    })
    .join(' ');
}

/**
 * Nearest real point to a pixel x — powers the crosshair.
 * Gap markers are skipped: the crosshair should never land on a hole and
 * report an em dash where the reader expects a reading.
 */
export function nearestIndex(points, x, pixelX) {
  if (!points.length) return -1;
  let best = -1;
  let bestDistance = Infinity;
  for (let i = 0; i < points.length; i += 1) {
    if (points[i].gap) continue;
    const distance = Math.abs(x(points[i].t) - pixelX);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = i;
    }
  }
  return best;
}

/** Time axis labels appropriate to the span being shown. */
export function timeTickFormat(spanMs) {
  if (spanMs <= 6 * 3600e3) {
    return (t) => new Date(t).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  }
  if (spanMs <= 3 * 86400e3) {
    return (t) =>
      new Date(t).toLocaleString(undefined, { weekday: 'short', hour: '2-digit' });
  }
  return (t) => new Date(t).toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
}

/** Evenly spaced tick positions across a time domain. */
export function timeTicks(domain, count = 5) {
  const [start, end] = domain;
  const step = (end - start) / Math.max(1, count - 1);
  return Array.from({ length: count }, (_, i) => start + i * step);
}
